 
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import Toast from '../shared/Toast.jsx';
import StaleBanner from '../shared/StaleBanner.jsx';
 
import { useRealtime } from '../../hooks/useRealtime.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useAgentStore } from '../../store/agentStore.jsx';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../../lib/api.js';
import {
  addToast as addToastToQueue,
  expireToasts,
  removeToast,
  visibleToasts,
} from '../../lib/toastQueue.js';

const TOAST_TTL_MS = 5000;
const TOAST_SWEEP_MS = 500;

export default function AppShell() {
  const { impersonating, endImpersonation } = useAuth();
  const { agentId } = useAgentStore();
  const { connected, connectionState, agentStatus, lastEvent } = useRealtime(agentId);
  const queryClient = useQueryClient();
  const [toasts, setToasts] = useState([]);
  // Monotonic id source. `Date.now()` was used here and it COLLIDED: alerts
  // arrive in bursts, two in the same millisecond shared an id, which gave
  // React duplicate keys and made the first expiry sweep remove every toast
  // sharing that id — toasts vanished early and unpredictably.
  const toastId = useRef(0);

  // Debounced query invalidation — coalesce rapid WebSocket events
  const pendingInvalidations = useRef(new Set());
  const debounceTimer = useRef(null);

  const flushInvalidations = useCallback(() => {
    for (const key of pendingInvalidations.current) {
      queryClient.invalidateQueries({ queryKey: JSON.parse(key) });
    }
    pendingInvalidations.current.clear();
  }, [queryClient]);

  const scheduleInvalidation = useCallback((...queryKey) => {
    pendingInvalidations.current.add(JSON.stringify(queryKey));
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(flushInvalidations, 300);
  }, [flushInvalidations]);

  useEffect(() => {
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, []);

  // Wire real-time events into TanStack Query cache
  useEffect(() => {
    if (!lastEvent) return;

    switch (lastEvent.type) {
      case 'device.status_change':
        scheduleInvalidation('devices', agentId);
        scheduleInvalidation('device', agentId, lastEvent.device_id);
        break;

      case 'alert.triggered': {
        // Invalidate the legacy agent-scoped alerts cache AND the
        // S11-6 ['alerts', ...] tree (list/count/recent-resolved).
        // Partial-match on ['alerts'] picks up every sub-key.
        scheduleInvalidation('alerts', agentId);
        scheduleInvalidation('alerts');
        // The agent's cloud_ws.rs wraps StateChangeEvent as
        // `{ type, data: <event> }` — message + severity live on
        // `lastEvent.data`, not the top level. The pre-fix
        // `lastEvent.message` was always undefined, rendering as
        // 'Alert: undefined' (literal). Fall back to top-level so
        // any future wrapper change that flattens fields still works.
        //
        // vista#462 (S13-6 follow-up): post-S13-6 the agent's
        // template_resolver guarantees `payload.message` is always
        // non-empty (structured-default fallback even for NULL
        // templates — see template_resolver.rs's docstring + the
        // `required_fields_never_empty_or_undefined` test). So the
        // pre-S13-6 `|| payload.rule_type || 'fired'` fallback chain
        // is dead code. Kept as a single-step fallback ONLY for the
        // wrapper-change case where `.data` is somehow missing.
        const payload = lastEvent.data || lastEvent;
        addToast(`Alert: ${payload.message}`, payload.severity || 'warning');
        break;
      }

      case 'alert.resolved':
        scheduleInvalidation('alerts', agentId);
        scheduleInvalidation('alerts');
        break;

      case 'topology.changed':
        scheduleInvalidation('topology', agentId);
        break;

      case 'health.updated':
      case 'collector.status':
        scheduleInvalidation('health', agentId);
        break;

      case 'agent.status':
        scheduleInvalidation('agents-list');
        break;
    }
  }, [lastEvent, agentId, scheduleInvalidation]);

  // Coalesce + cap rather than append-per-frame. See lib/toastQueue.js for why
  // (and for the reducers' tests). No per-toast setTimeout: one sweep below
  // handles expiry, so a burst no longer schedules a timer per event.
  function addToast(message, severity = 'info') {
    toastId.current += 1;
    setToasts((prev) =>
      addToastToQueue(prev, {
        message,
        severity,
        id: toastId.current,
        seq: Date.now(),
      }),
    );
  }

  function dismissToast(id) {
    setToasts((prev) => removeToast(prev, id));
  }

  // Single expiry sweep. Runs only while toasts exist, so an idle dashboard
  // holds no interval. Expiry is computed from each toast's `seq`, which
  // coalescing refreshes — a device flapping every 2s keeps ONE card alive
  // instead of leaving a trail of separately-expiring ones.
  useEffect(() => {
    if (toasts.length === 0) return undefined;
    const t = setInterval(() => {
      setToasts((prev) => expireToasts(prev, Date.now(), TOAST_TTL_MS));
    }, TOAST_SWEEP_MS);
    return () => clearInterval(t);
  }, [toasts.length]);

  // Subscribe to agents-list so the banner re-renders when the query resolves
  // or is invalidated (agent connects, status flips from pending → active).
  // TanStack Query dedupes by key — Header.jsx's query stays the single fetch.
  const { data: agents } = useQuery({
    queryKey: ['agents-list'],
    queryFn: () => api.get('/agents'),
  });
  const selectedAgent = Array.isArray(agents) ? agents.find((a) => a.agent_id === agentId) : null;
  const agentPending = selectedAgent?.status === 'pending' && agentStatus !== 'online';
  // The browser WebSocket to the Durable Object connects even for pending
  // agents (the DO accepts the browser before any upstream is live), so
  // `!connected` alone would hide the banner for the exact case it was added
  // to surface. Also show it whenever the agent is still pending.
  const showOfflineBanner = agentId && (!connected || agentPending);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        {impersonating && (
          <div className="flex items-center justify-between bg-amber-500 px-4 py-2 text-sm font-medium text-white">
            <span>Impersonating <strong>{impersonating.org_name || impersonating.org_id}</strong> as org_admin</span>
            <button
              onClick={async () => {
                await endImpersonation();
                // This tab was opened via window.open from the admin console,
                // so close it. If the browser blocks the close, endImpersonation
                // already restored the vista_admin session which triggers the
                // AuthGate "use Admin Console" message.
                window.close();
              }}
              className="rounded-md border border-white/50 bg-white/10 px-3 py-1 text-xs font-semibold text-white hover:bg-white/20"
            >
              End Session
            </button>
          </div>
        )}
        <Header connected={connected} agentStatus={agentStatus} />
        {/* #786: HTTP data-freshness signal (distinct from the WS-connection
            offline banner below) — fires when a response was served
            stale-while-revalidate, and shows the data age. */}
        <StaleBanner />
        {showOfflineBanner && (
          <div className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white ${agentPending ? 'bg-blue-500' : 'bg-red-500'}`}>
            <span className="inline-block h-2 w-2 rounded-full bg-white opacity-75" />
            {agentPending
              ? 'Waiting for agent to connect. Complete the agent setup and start the agent service to begin monitoring.'
              : connectionState === 'connecting'
                ? 'Reconnecting to agent...'
                : 'Agent connection lost. Data may be stale. Reconnecting...'}
          </div>
        )}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
        <Footer />
      </div>
      <Toast {...visibleToasts(toasts)} onDismiss={dismissToast} />
    </div>
  );
}
